// SAB-004 — Canonical confidence & evidence vocabulary.
//
// ONE ladder, ONE label set, ONE meaning for every confidence indicator that
// ClaimGuard renders. This module is presentation-only: it never computes a
// confidence value, never invents one, and never supplies a numeric default.
// The scoring engines (confidence-engine, quality-engine, readiness-engine,
// the vendor investigation coverage engine, and the intel match engines) are
// unchanged — they produce numbers; this module is the only place allowed to
// turn a number into a word shown to a customer.
//
// Rules:
//   1. A confidence indicator with no measured value renders NOT_CALCULATED.
//      Never substitute a plausible-looking percentage.
//   2. Every displayed confidence must state WHAT it measures. The report-wide
//      analysis confidence and a per-source match confidence are different
//      quantities and may not share a bare "Confidence" label.
//   3. Only the bands below may be shown. No parallel ladders.

/** The canonical four-band ladder, high to low. */
export type ConfidenceBand = "Very High" | "High" | "Moderate" | "Limited";

/** Canonical cut points on a 0–100 scale. The single source of truth. */
export const CONFIDENCE_BAND_MIN: Record<Exclude<ConfidenceBand, "Limited">, number> = {
  "Very High": 90,
  High: 75,
  Moderate: 55,
};

/** Text shown wherever a confidence value is unavailable. */
export const NOT_CALCULATED = "Not Calculated";

/**
 * Canonical label for the report-wide analysis confidence reported by the
 * findings engine. Used verbatim on the review page, the executive summary,
 * the operations workspace, and the reliability tiles so the same number is
 * never introduced under three different names.
 */
export const ANALYSIS_CONFIDENCE_LABEL = "Analysis Confidence";

/** Normalize a 0–1 fraction or a 0–100 percentage into 0–100, or null. */
export function toConfidencePct(raw: number | null | undefined): number | null {
  if (typeof raw !== "number" || !Number.isFinite(raw) || raw <= 0) return null;
  const pct = raw <= 1 ? raw * 100 : raw;
  return Math.min(100, Math.round(pct));
}

/** Map a 0–100 confidence to its canonical band. Null in ⇒ null out. */
export function confidenceBand(pct: number | null | undefined): ConfidenceBand | null {
  if (typeof pct !== "number" || !Number.isFinite(pct) || pct <= 0) return null;
  if (pct >= CONFIDENCE_BAND_MIN["Very High"]) return "Very High";
  if (pct >= CONFIDENCE_BAND_MIN.High) return "High";
  if (pct >= CONFIDENCE_BAND_MIN.Moderate) return "Moderate";
  return "Limited";
}

/** Map a 0–1 fraction to its canonical band. */
export function confidenceBandFromFraction(v: number | null | undefined): ConfidenceBand | null {
  return confidenceBand(toConfidencePct(v));
}

/** "Very High Confidence" … or NOT_CALCULATED when there is no signal. */
export function confidenceDescriptor(pct: number | null | undefined): string {
  const band = confidenceBand(pct);
  return band ? `${band} Confidence` : NOT_CALCULATED;
}

/** "98%" … or NOT_CALCULATED. Never renders 0%. */
export function formatConfidencePct(pct: number | null | undefined): string {
  const v = confidenceBand(pct) ? Math.round(pct as number) : null;
  return v == null ? NOT_CALCULATED : `${v}%`;
}

/**
 * Evidence-strength wording for the same ladder. Kept in lockstep with the
 * confidence bands so "Strong evidence" and "High confidence" can never be
 * derived from different cut points.
 */
export type EvidenceStrength = "Very Strong" | "Strong" | "Moderate" | "Limited";

const BAND_TO_STRENGTH: Record<ConfidenceBand, EvidenceStrength> = {
  "Very High": "Very Strong",
  High: "Strong",
  Moderate: "Moderate",
  Limited: "Limited",
};

/** Canonical evidence strength from a 0–100 confidence, or null when absent. */
export function evidenceStrength(pct: number | null | undefined): EvidenceStrength | null {
  const band = confidenceBand(pct);
  return band ? BAND_TO_STRENGTH[band] : null;
}

/** Canonical evidence strength from a 0–1 relevance/confidence fraction. */
export function evidenceStrengthFromFraction(
  v: number | null | undefined,
): EvidenceStrength | null {
  return evidenceStrength(toConfidencePct(v));
}

/**
 * Minimum match confidence at which a provider match may be badged as a high
 * confidence match. Named so the intel chapters stop carrying private copies
 * of the same literal. This is a MATCH-quality gate, deliberately distinct
 * from the analysis-confidence ladder above, and must always be displayed
 * with "Match" wording rather than a bare "Confidence" label.
 */
export const MATCH_HIGH_CONFIDENCE_MIN = 0.85;
